/**
 * Canary origin logic — PURE and side-effect-free, so it is unit-testable.
 *
 * Decides whether the running app sits on an origin we deploy to legitimately.
 * If not, this build is running somewhere it should not be — a redeployed stolen
 * copy — and `canary-probe.ts` reports it to the backend `canaryBeacon`.
 *
 * See backend/functions/src/security/canaryBeacon.ts for the receiving end.
 */

/**
 * Shared identifier the app bundle carries so the backend can drop random spam
 * to the public beacon endpoint. NOT a secret — it ships in the public bundle
 * and only gates spam. MIRROR of `CANARY_BEACON_ID` in the backend `.env`
 * files (.env.proporio-dev / .env.proporio-849bb); rotate both together.
 */
export const CANARY_BEACON_ID = 'proporio-canary-v1';

export interface CanaryFirebaseConfig {
  projectId: string;
  functionsRegion?: string;
}

/**
 * True when `hostname` is an authorized origin:
 *  - local dev hosts: localhost, 127.0.0.1, ::1, *.localhost, *.test
 *  - propor.io and any subdomain (dev.propor.io, app.propor.io, …)
 *  - THIS project's own Firebase domains (<projectId>.web.app / .firebaseapp.com)
 *
 * Everything else is foreign. An empty hostname (file://, SSR, tests) returns
 * true — we never report when we cannot tell where we are.
 */
export function isAuthorizedCanaryOrigin(hostname: string, projectId: string): boolean {
  const h = (hostname || '').toLowerCase().replace(/^\[/, '').replace(/\]$/, '');
  if (!h) return true;

  if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return true;
  if (h.endsWith('.localhost') || h.endsWith('.test')) return true;

  if (h === 'propor.io' || h.endsWith('.propor.io')) return true;

  const pid = (projectId || '').toLowerCase();
  if (pid && (h === `${pid}.web.app` || h === `${pid}.firebaseapp.com`)) return true;

  return false;
}

/** Absolute URL of the gen2 canary beacon Cloud Function for this project. */
export function buildCanaryBeaconUrl(cfg: CanaryFirebaseConfig): string {
  const region = cfg.functionsRegion || 'europe-west1';
  return `https://${region}-${cfg.projectId}.cloudfunctions.net/canaryBeacon`;
}
